import { Select, SelectProps } from 'antd';
import { useGetPractitionerList } from 'src/hooks/practitioner';
import styled from '@emotion/styled';
import { PractitionerName } from './practitionerName';
import { UserX01 } from '@insight-health-ai/roseate/icons';
import { UserOutlined } from '@ant-design/icons';
import { useMemo, useState } from 'react';
import { useGetCurrentPractitioner } from 'src/hooks/settings';
import { Practitioner } from 'src/models';

const StyledSelect = styled(Select)`
  .ant-select-prefix {
    margin-right: 8px;
  }
  ${({ variant }) =>
    variant === 'borderless' &&
    `
      & .ant-select-selector {
        padding-left: 0 !important;
      }
    `}
`;

type PractitionerDropdownProps = Pick<
  SelectProps,
  | 'className'
  | 'value'
  | 'onChange'
  | 'loading'
  | 'disabled'
  | 'showSearch'
  | 'variant'
  | 'mode'
  | 'prefix'
  | 'placeholder'
  | 'allowClear'
> & {
  labelRender?: (
    props: Parameters<Exclude<SelectProps['labelRender'], undefined>>[0],
    state: {
      isOpen: boolean;
      isLoading: boolean;
    }
  ) => React.ReactNode;
};

const defaultLabelRendere: PractitionerDropdownProps['labelRender'] = ({
  label,
}) => {
  return label ? (
    label
  ) : (
    <span className="text-gray-500 flex items-center gap-2">
      <UserX01 /> Hidden
    </span>
  );
};

export const PractitionerDropdown = (props: PractitionerDropdownProps) => {
  const { labelRender: labelRenderProp, value, ...rest } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { data: practitionerList, isLoading: isLoadingPractitionerList } =
    useGetPractitionerList({
      is_shown: true,
    });

  const { data: currentPractitioner } = useGetCurrentPractitioner();

  const labelRender = labelRenderProp || defaultLabelRendere;

  const options = useMemo(() => {
    const list = [];
    if (currentPractitioner) {
      const practitioner = {
        practitioner_id: currentPractitioner.practitioner_id,
        first_name: currentPractitioner.practitioner_first_name,
        last_name: currentPractitioner.practitioner_last_name,
        full_name: currentPractitioner.practitioner_name,
      } as Practitioner;
      list.push({
        label: <PractitionerName practitioner={practitioner} />,
        value: practitioner.practitioner_id,
        practitioner,
      });
    }
    practitionerList?.practitioners.forEach((practitioner) => {
      if (
        practitioner.practitioner_id === currentPractitioner?.practitioner_id
      ) {
        return;
      }
      list.push({
        label: <PractitionerName practitioner={practitioner} />,
        value: practitioner.practitioner_id,
        practitioner,
      });
    });
    return list;
  }, [practitionerList, currentPractitioner]);

  return (
    <StyledSelect
      placeholder="Select Practitioner"
      {...rest}
      value={value}
      popupMatchSelectWidth={false}
      prefix={!value && <UserOutlined />}
      filterOption={(input, option) => {
        return (option as any)?.practitioner.full_name
          ?.toLowerCase()
          .includes(input.toLowerCase());
      }}
      onOpenChange={setIsOpen}
      labelRender={(option) => {
        return labelRender?.(option, {
          isOpen,
          isLoading: isLoadingPractitionerList,
        });
      }}
      options={options}
    />
  );
};
